import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { CardGrid } from '../components/CardGrid';
import FloatingNav from '../components/FloatingNav';
import {
  Smartphone, Trophy, Users, Gift, Shield, BarChart3, MessageSquare,
  Play, Tv, Home, Eye, Star, Clock, Lock, ChevronRight, Zap, Target,
  Award, UserCheck, CreditCard, Settings, Volume2
} from 'lucide-react';

const VIDEO_URL = 'https://customer-assets-0z36b82j.emergentagent.net/job_tap-platform-build/artifacts/c7c4f4a0ded60688_TapNWin_Sports_Ad_9x16_v20_EN.mp4';

/* ═══════════════════════════════════════════════════════════════
   TRILINGUAL CONTENT — sourced from the two attached PDFs only
   ═══════════════════════════════════════════════════════════════ */
const T = {
  en: {
    seoTitle: "Tap'N Win Sports | Every Goal Fills the Room | YUL On Tap",
    seoDesc: "A live game your guests play on their own phones during the game. Every goal opens a scratch card. Prizes bring them back.",
    heroTitle: "Tap'N Win Sports.",
    heroSub: "Every goal fills the room. Every prize brings them back.",
    heroCta: "Request a Demo",
    heroVideo: "Watch the Video",

    whatBadge: "WHAT IS TAP'N WIN",
    whatTitle: "A live game your guests play on their own phones during the game.",
    whatP1: "From the bar or from home, no app to download. All a guest needs is an email, a screen name and a 4-digit code.",
    whatP2: "Each guest picks tonight's game and the team they think scores. When a goal is scored, a scratch card opens for every player — with prizes the venue chooses: a free round, an appetizer, a gift card.",
    whatP3: "Patriot Points and return gift cards give them a reason to come back for the next game.",
    whatCards: [
      { icon: Smartphone, t: "No app to download" },
      { icon: Gift, t: "Prizes you choose" },
      { icon: Award, t: "Patriot Points" },
      { icon: CreditCard, t: "Return gift cards" },
    ],

    videoBadge: "SEE IT IN ACTION",
    videoTitle: "Watch the Tap'N Win Promo",
    videoNote: null,

    howBadge: "HOW IT WORKS",
    howTitle: "From table to prize in four steps.",
    howSteps: [
      { num: "1", title: "Guests join", desc: "A table card or your social link. Just an email and a screen name — no app." },
      { num: "2", title: "They pick", desc: "The game, the team and a star player. A correct star pick doubles the prize." },
      { num: "3", title: "Goal! They scratch", desc: "A scratch card opens for every player in a five-minute window." },
      { num: "4", title: "Win", desc: "In-house prizes for the whole table, or gift cards to redeem on the next visit." },
    ],

    barHomeBadge: "BAR OR HOME",
    barHomeTitle: "The whole crowd plays, bar and couch.",
    barTitle: "In the bar",
    barItems: [
      "Tap the table card and win live prizes that night: wings, pitchers, nachos.",
      "Table prizes go to the table. Points go to each player.",
    ],
    homeTitle: "At home",
    homeItems: [
      "Players sign in from a game-day link.",
      "They win gift cards for their next visit — a reason to walk through your door.",
    ],
    barHomeNote: "Every prize is set to In the bar, At home, or Both — you decide who can win what.",

    boardsBadge: "LIVE BOARDS",
    boardsTitle: "Live board, leaderboard and chat.",
    boardItems: [
      { icon: Users, title: "Live players board", desc: "Everyone on one board — winner pick, star, points and wins. Pick accuracy green above 50%, red below." },
      { icon: Trophy, title: "Leaderboard", desc: "Rank by points, pick accuracy or wins — for tonight or all time." },
      { icon: MessageSquare, title: "Chat", desc: "Optional, and yours to switch on. Staff can delete and mute." },
    ],
    boardExtra: "Pinned announcements let you push specials live to every screen.",

    venBadge: "FOR BARS AND RESTAURANTS",
    venTitle: "The opportunity for bars and restaurants.",
    venItems: [
      { icon: Users, title: "Fuller game nights", desc: "A reason to pick your place, and to stay for the next goal." },
      { icon: Award, title: "Guests who come back", desc: "Return gift cards and Patriot Points give them a reason to return." },
      { icon: Target, title: "Players from your own social", desc: "Post your venue's join link and sign up fans right away." },
      { icon: Settings, title: "Prizes and odds set by you", desc: "You pick what is won and how often, so the cost is yours to control." },
      { icon: Shield, title: "Gift Guard", desc: "Money-value gift cards activated by one text, one player per phone." },
    ],
    venOwnerTitle: "Owner tools",
    venOwnerItems: [
      "Live Game View — tonight's game, score and clock, with every player as they register.",
      "Game Results — who played, who won what and how often, time-stamped.",
      "Player profiles — past games, wins and redemptions.",
      "Bar vs home — who joined from a table and who joined from a link.",
      "Staff redemption record — which employee redeemed each prize, and when.",
    ],

    sponBadge: "FOR SUPPLIER SPONSORS",
    sponTitle: "The opportunity for supplier sponsors.",
    sponItems: [
      { icon: Eye, title: "On every screen", desc: "A sponsor's logo sits on every player screen — from the scratch card to the standings to the gift card." },
      { icon: Gift, title: "Fund or supply prizes", desc: "Sponsors can fund or supply prizes for the venue's game nights." },
      { icon: Target, title: "Tied to venues or games", desc: "A sponsor can be tied to specific venues or specific games." },
      { icon: Zap, title: "Duplicate for a new sport", desc: "One promotion can be duplicated for a new sport or sponsor — change only the words." },
      { icon: Tv, title: "Live fans, in venues and at home", desc: "Reach is live fans watching in venues and at home under your name." },
    ],
    sponCta: "Become a Sponsor",

    builtBadge: "BUILT FOR REAL VENUES",
    builtTitle: "Control stays with your team.",
    builtItems: [
      { icon: UserCheck, title: "Staff set the table", desc: "Staff register each table with their own code. It is logged by name and time." },
      { icon: Lock, title: "Private by design", desc: "Player boards never show email addresses." },
      { icon: Volume2, title: "Chat on your terms", desc: "Off until you switch it on. Staff can delete and mute, with a 7-day audit." },
    ],

    closeBadge: "GET STARTED",
    closeTitle: "Put your next game night on the board.",
    closeCta: "Request a Demo",
    closeEmail: "jason@yulontap.com",

    smallPrint: "Tap'N Win Sports is a product of Tapelligence. Prizes are provided by the participating venue. Product screens and names shown are illustrative.",
    langLabel: "Also available in:",
    langLinks: [
      { label: "Français", path: "/fr/tapez-et-gagnez" },
      { label: "Español", path: "/es/tap-n-win" },
    ],
  },

  fr: {
    seoTitle: "Tapez et Gagnez Sports | Chaque but remplit la salle | YUL On Tap",
    seoDesc: "Un jeu en direct que vos clients jouent sur leur propre téléphone pendant le match. Chaque but ouvre une carte à gratter. Les prix les font revenir.",
    heroTitle: "Tapez et Gagnez Sports.",
    heroSub: "Chaque but remplit la salle. Chaque prix les fait revenir.",
    heroCta: "Demander une démo",
    heroVideo: "Voir la vidéo",

    whatBadge: "QU'EST-CE QUE TAPEZ ET GAGNEZ",
    whatTitle: "Un jeu en direct que vos clients jouent sur leur propre téléphone pendant le match.",
    whatP1: "Du bar ou de la maison, aucune application à télécharger. Tout ce qu'il faut : un courriel, un pseudonyme et un code à 4 chiffres.",
    whatP2: "Chaque client choisit le match de ce soir et l'équipe qui marquera. Quand un but est marqué, une carte à gratter s'ouvre pour chaque joueur — avec des prix choisis par l'établissement : une tournée, une entrée, une carte-cadeau.",
    whatP3: "Les Points Patriot et les cartes-cadeaux retour leur donnent une raison de revenir pour le prochain match.",
    whatCards: [
      { icon: Smartphone, t: "Aucune appli à télécharger" },
      { icon: Gift, t: "Des prix que vous choisissez" },
      { icon: Award, t: "Points Patriot" },
      { icon: CreditCard, t: "Cartes-cadeaux retour" },
    ],

    videoBadge: "EN ACTION",
    videoTitle: "Regardez la promo Tapez et Gagnez",
    videoNote: "La vidéo est en anglais.",

    howBadge: "COMMENT ÇA MARCHE",
    howTitle: "De la table au prix en quatre étapes.",
    howSteps: [
      { num: "1", title: "Les clients rejoignent", desc: "Une carte de table ou votre lien social. Un courriel et un pseudonyme — aucune appli." },
      { num: "2", title: "Ils choisissent", desc: "Le match, l'équipe et un joueur étoile. Un bon choix d'étoile double le prix." },
      { num: "3", title: "But! Ils grattent", desc: "Une carte à gratter s'ouvre pour chaque joueur dans une fenêtre de cinq minutes." },
      { num: "4", title: "Gagné", desc: "Des prix sur place pour toute la table, ou des cartes-cadeaux pour la prochaine visite." },
    ],

    barHomeBadge: "BAR OU MAISON",
    barHomeTitle: "Toute la foule joue, bar et divan.",
    barTitle: "Au bar",
    barItems: [
      "Tapez la carte de table et gagnez des prix en direct ce soir-là : ailes, pichets, nachos.",
      "Les prix de table vont à la table. Les points vont à chaque joueur.",
    ],
    homeTitle: "À la maison",
    homeItems: [
      "Les joueurs se connectent depuis un lien de jour de match.",
      "Ils gagnent des cartes-cadeaux pour leur prochaine visite — une raison de passer chez vous.",
    ],
    barHomeNote: "Chaque prix est défini Au bar, À la maison, ou Les deux — c'est vous qui décidez qui peut gagner quoi.",

    boardsBadge: "TABLEAUX EN DIRECT",
    boardsTitle: "Tableau en direct, classement et clavardage.",
    boardItems: [
      { icon: Users, title: "Tableau des joueurs en direct", desc: "Tout le monde sur un même tableau — choix du gagnant, étoile, points et victoires." },
      { icon: Trophy, title: "Classement", desc: "Classez par points, précision des choix ou victoires — ce soir ou de tous les temps." },
      { icon: MessageSquare, title: "Clavardage", desc: "Optionnel, et c'est vous qui l'activez. Le personnel peut supprimer et rendre muet." },
    ],
    boardExtra: "Les annonces épinglées vous permettent de pousser des spéciaux en direct sur chaque écran.",

    venBadge: "POUR LES BARS ET RESTAURANTS",
    venTitle: "L'opportunité pour les bars et restaurants.",
    venItems: [
      { icon: Users, title: "Des soirées de match plus remplies", desc: "Une raison de choisir votre place, et de rester pour le prochain but." },
      { icon: Award, title: "Des clients qui reviennent", desc: "Les cartes-cadeaux retour et les Points Patriot leur donnent une raison de revenir." },
      { icon: Target, title: "Des joueurs depuis vos réseaux sociaux", desc: "Publiez votre lien d'inscription et inscrivez des fans immédiatement." },
      { icon: Settings, title: "Prix et probabilités définis par vous", desc: "Vous choisissez ce qui est gagné et à quelle fréquence — le coût est sous votre contrôle." },
      { icon: Shield, title: "Gift Guard", desc: "Cartes-cadeaux à valeur monétaire activées par un texto, un joueur par téléphone." },
    ],
    venOwnerTitle: "Outils du propriétaire",
    venOwnerItems: [
      "Vue du match en direct — le match, le pointage et l'horloge, avec chaque joueur dès son inscription.",
      "Résultats du match — qui a joué, qui a gagné quoi et à quelle fréquence, horodatés.",
      "Profils des joueurs — matchs passés, victoires et rachats.",
      "Bar vs maison — qui s'est joint depuis une table et qui s'est joint depuis un lien.",
      "Registre du personnel — quel employé a racheté chaque prix, et quand.",
    ],

    sponBadge: "POUR LES COMMANDITAIRES",
    sponTitle: "L'opportunité pour les commanditaires fournisseurs.",
    sponItems: [
      { icon: Eye, title: "Sur chaque écran", desc: "Le logo du commanditaire apparaît sur chaque écran de joueur — de la carte à gratter au classement en passant par la carte-cadeau." },
      { icon: Gift, title: "Financer ou fournir des prix", desc: "Les commanditaires peuvent financer ou fournir des prix pour les soirées de match." },
      { icon: Target, title: "Lié à des établissements ou des matchs", desc: "Un commanditaire peut être lié à des établissements ou des matchs spécifiques." },
      { icon: Zap, title: "Dupliquer pour un nouveau sport", desc: "Une promotion peut être dupliquée pour un nouveau sport ou commanditaire — changez seulement les mots." },
      { icon: Tv, title: "Des fans en direct, au bar et à la maison", desc: "La portée couvre les fans en direct dans les établissements et à la maison sous votre nom." },
    ],
    sponCta: "Devenir commanditaire",

    builtBadge: "CONÇU POUR DE VRAIS ÉTABLISSEMENTS",
    builtTitle: "Le contrôle reste avec votre équipe.",
    builtItems: [
      { icon: UserCheck, title: "Le personnel configure la table", desc: "Le personnel inscrit chaque table avec son propre code. C'est journalisé par nom et heure." },
      { icon: Lock, title: "Privé par conception", desc: "Les tableaux de joueurs ne montrent jamais les adresses courriel." },
      { icon: Volume2, title: "Clavardage à vos conditions", desc: "Désactivé jusqu'à ce que vous l'activiez. Le personnel peut supprimer et rendre muet, avec un audit de 7 jours." },
    ],

    closeBadge: "COMMENCER",
    closeTitle: "Mettez votre prochaine soirée de match au tableau.",
    closeCta: "Demander une démo",
    closeEmail: "jason@yulontap.com",

    smallPrint: "Tapez et Gagnez Sports est un produit de Tapelligence. Les prix sont fournis par l'établissement participant. Les écrans et noms de produits montrés sont illustratifs.",
    langLabel: "Aussi disponible en :",
    langLinks: [
      { label: "English", path: "/en/tap-n-win" },
      { label: "Español", path: "/es/tap-n-win" },
    ],
  },

  es: {
    seoTitle: "Tap'N Win Sports | Cada gol llena el lugar | YUL On Tap",
    seoDesc: "Un juego en vivo que tus clientes juegan en sus propios teléfonos durante el partido. Cada gol abre una tarjeta de rascar. Los premios los hacen volver.",
    heroTitle: "Tap'N Win Sports.",
    heroSub: "Cada gol llena el lugar. Cada premio los hace volver.",
    heroCta: "Solicitar una demo",
    heroVideo: "Ver el video",

    whatBadge: "QUÉ ES TAP'N WIN",
    whatTitle: "Un juego en vivo que tus clientes juegan en sus propios teléfonos durante el partido.",
    whatP1: "Desde el bar o desde casa, sin aplicación que descargar. Solo necesitan un correo electrónico, un nombre de usuario y un código de 4 dígitos.",
    whatP2: "Cada cliente elige el partido de esta noche y el equipo que cree que anotará. Cuando se marca un gol, se abre una tarjeta de rascar para cada jugador — con premios que el establecimiento elige: una ronda gratis, un aperitivo, una tarjeta de regalo.",
    whatP3: "Los Puntos Patriot y las tarjetas de regalo de retorno les dan una razón para volver al próximo partido.",
    whatCards: [
      { icon: Smartphone, t: "Sin app que descargar" },
      { icon: Gift, t: "Premios que tú eliges" },
      { icon: Award, t: "Puntos Patriot" },
      { icon: CreditCard, t: "Tarjetas de regalo retorno" },
    ],

    videoBadge: "VÉALO EN ACCIÓN",
    videoTitle: "Vea la promo de Tap'N Win",
    videoNote: "El video está en inglés.",

    howBadge: "CÓMO FUNCIONA",
    howTitle: "De la mesa al premio en cuatro pasos.",
    howSteps: [
      { num: "1", title: "Los clientes se unen", desc: "Una tarjeta de mesa o tu enlace social. Un correo y un nombre de usuario — sin app." },
      { num: "2", title: "Eligen", desc: "El partido, el equipo y un jugador estrella. Una elección correcta de estrella duplica el premio." },
      { num: "3", title: "¡Gol! Rascan", desc: "Se abre una tarjeta de rascar para cada jugador en una ventana de cinco minutos." },
      { num: "4", title: "Ganan", desc: "Premios en el lugar para toda la mesa, o tarjetas de regalo para la próxima visita." },
    ],

    barHomeBadge: "BAR O CASA",
    barHomeTitle: "Toda la multitud juega, bar y sofá.",
    barTitle: "En el bar",
    barItems: [
      "Toca la tarjeta de mesa y gana premios en vivo esa noche: alitas, jarras, nachos.",
      "Los premios de mesa van a la mesa. Los puntos van a cada jugador.",
    ],
    homeTitle: "En casa",
    homeItems: [
      "Los jugadores inician sesión desde un enlace del día del partido.",
      "Ganan tarjetas de regalo para su próxima visita — una razón para pasar por tu puerta.",
    ],
    barHomeNote: "Cada premio se configura como En el bar, En casa, o Ambos — tú decides quién puede ganar qué.",

    boardsBadge: "TABLEROS EN VIVO",
    boardsTitle: "Tablero en vivo, clasificación y chat.",
    boardItems: [
      { icon: Users, title: "Tablero de jugadores en vivo", desc: "Todos en un tablero — elección del ganador, estrella, puntos y victorias." },
      { icon: Trophy, title: "Clasificación", desc: "Ordena por puntos, precisión o victorias — esta noche o de todos los tiempos." },
      { icon: MessageSquare, title: "Chat", desc: "Opcional, y tú decides cuándo activarlo. El personal puede eliminar y silenciar." },
    ],
    boardExtra: "Los anuncios fijados te permiten enviar promociones en vivo a cada pantalla.",

    venBadge: "PARA BARES Y RESTAURANTES",
    venTitle: "La oportunidad para bares y restaurantes.",
    venItems: [
      { icon: Users, title: "Noches de partido más llenas", desc: "Una razón para elegir tu lugar, y para quedarse para el próximo gol." },
      { icon: Award, title: "Clientes que regresan", desc: "Las tarjetas de regalo de retorno y los Puntos Patriot les dan razones para volver." },
      { icon: Target, title: "Jugadores desde tus redes sociales", desc: "Publica tu enlace de registro e inscribe fans de inmediato." },
      { icon: Settings, title: "Premios y probabilidades los defines tú", desc: "Tú eliges qué se gana y con qué frecuencia — el costo está bajo tu control." },
      { icon: Shield, title: "Gift Guard", desc: "Tarjetas de regalo con valor activadas por un texto, un jugador por teléfono." },
    ],
    venOwnerTitle: "Herramientas del propietario",
    venOwnerItems: [
      "Vista del partido en vivo — el partido, marcador y reloj, con cada jugador al registrarse.",
      "Resultados del partido — quién jugó, quién ganó qué y con qué frecuencia, con marca de tiempo.",
      "Perfiles de jugadores — partidos pasados, victorias y canjes.",
      "Bar vs casa — quién se unió desde una mesa y quién desde un enlace.",
      "Registro del personal — qué empleado canjeó cada premio, y cuándo.",
    ],

    sponBadge: "PARA PATROCINADORES",
    sponTitle: "La oportunidad para patrocinadores proveedores.",
    sponItems: [
      { icon: Eye, title: "En cada pantalla", desc: "El logo del patrocinador aparece en cada pantalla — desde la tarjeta de rascar hasta la clasificación y la tarjeta de regalo." },
      { icon: Gift, title: "Financiar o proveer premios", desc: "Los patrocinadores pueden financiar o proveer premios para las noches de partido." },
      { icon: Target, title: "Vinculado a establecimientos o partidos", desc: "Un patrocinador puede vincularse a establecimientos o partidos específicos." },
      { icon: Zap, title: "Duplicar para un nuevo deporte", desc: "Una promoción puede duplicarse para un nuevo deporte o patrocinador — solo cambia las palabras." },
      { icon: Tv, title: "Fans en vivo, en el lugar y en casa", desc: "El alcance cubre fans en vivo en establecimientos y en casa bajo tu nombre." },
    ],
    sponCta: "Convertirse en patrocinador",

    builtBadge: "HECHO PARA ESTABLECIMIENTOS REALES",
    builtTitle: "El control queda con tu equipo.",
    builtItems: [
      { icon: UserCheck, title: "El personal configura la mesa", desc: "El personal registra cada mesa con su propio código. Se registra por nombre y hora." },
      { icon: Lock, title: "Privado por diseño", desc: "Los tableros de jugadores nunca muestran correos electrónicos." },
      { icon: Volume2, title: "Chat en tus términos", desc: "Desactivado hasta que lo actives. El personal puede eliminar y silenciar, con auditoría de 7 días." },
    ],

    closeBadge: "EMPEZAR",
    closeTitle: "Pon tu próxima noche de partido en el tablero.",
    closeCta: "Solicitar una demo",
    closeEmail: "jason@yulontap.com",

    smallPrint: "Tap'N Win Sports es un producto de Tapelligence. Los premios son proporcionados por el establecimiento participante. Las pantallas y nombres de productos mostrados son ilustrativos.",
    langLabel: "También disponible en:",
    langLinks: [
      { label: "English", path: "/en/tap-n-win" },
      { label: "Français", path: "/fr/tapez-et-gagnez" },
    ],
  },
};

/* ═══════════════════════════════════════════════════════════════ */

const ROUTES = {
  '/en/tap-n-win': 'en',
  '/fr/tapez-et-gagnez': 'fr',
  '/es/tap-n-win': 'es',
};

export function getTapNWinPath(lang) {
  if (lang === 'fr') return '/fr/tapez-et-gagnez';
  if (lang === 'es') return '/es/tap-n-win';
  return '/en/tap-n-win';
}

export function isTapNWinPath(pathname) {
  return !!ROUTES[pathname];
}

export function getTapNWinLang(pathname) {
  return ROUTES[pathname] || null;
}

const IMG = '/tapnwin/';
const ALT = {
  en: { gift: "Tap'N Win return gift card for the next visit" },
  fr: { gift: "Carte-cadeau de retour Tapez et Gagnez pour la prochaine visite" },
  es: { gift: "Tarjeta de regalo de regreso Tap'N Win para la próxima visita" },
};
const PHONES = [
  { id: 'pick', src: 'tapnwin_phone_pick_winner_star.png', w: 455, h: 904 },
  { id: 'luck', src: 'tapnwin_phone_good_luck.png', w: 455, h: 904 },
  { id: 'goal', src: 'tapnwin_phone_goal_scratch.png', w: 433, h: 852 },
];
const PHONE_CAP = {
  en: { pick: 'Pick your winner and star', luck: 'Good luck', goal: 'Goal! Scratch and win' },
  fr: { pick: 'Choisissez votre gagnant et votre étoile', luck: 'Bonne chance', goal: 'But ! Grattez et gagnez' },
  es: { pick: 'Elige tu ganador y tu estrella', luck: 'Buena suerte', goal: '¡Gol! Rasca y gana' },
};
const NAV_SECTIONS = [
  { id: 'tnw-what', labelEn: 'What it is', labelFr: "Qu'est-ce que c'est", labelEs: 'Qué es' },
  { id: 'tnw-how', labelEn: 'How it works', labelFr: 'Comment ça marche', labelEs: 'Cómo funciona' },
  { id: 'tnw-bar-home', labelEn: 'Bar or home', labelFr: 'Bar ou maison', labelEs: 'Bar o casa' },
  { id: 'tnw-venues', labelEn: 'For bars and restaurants', labelFr: 'Pour les bars et restaurants', labelEs: 'Para bares y restaurantes' },
  { id: 'tnw-sponsors', labelEn: 'For sponsors', labelFr: 'Pour les commanditaires', labelEs: 'Para patrocinadores' },
];
const BOARD_IMGS = [
  { src: 'tapnwin_live_players_board.png', w: 1398, h: 668, id: 'live-players' },
  { src: 'tapnwin_leaderboard.png', w: 1052, h: 676, id: 'leaderboard' },
  { src: 'tapnwin_chat.png', w: 1052, h: 676, id: 'chat' },
];

const Shot = ({ src, w, h, alt, testId, className = '' }) => (
  <img src={IMG + src} width={w} height={h} alt={alt} loading="lazy" data-testid={testId}
    className={`block w-full h-auto max-w-full rounded-xl ${className}`} />
);

/* ── Reusable section primitives ── */


const Badge = ({ children }) => (
  <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 rounded-full border border-white/10 text-xs font-bold uppercase tracking-[0.2em] text-white/50 mb-6">
    {children}
  </span>
);

const SectionCard = ({ icon: Icon, title, desc }) => (
  <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-6 hover:border-white/20 transition-all">
    <div className="w-10 h-10 rounded-lg bg-wine/15 flex items-center justify-center mb-4">
      <Icon className="w-5 h-5 text-wine" strokeWidth={1.5} />
    </div>
    <h3 className="text-white font-medium mb-2">{title}</h3>
    <p className="text-white/50 text-sm leading-relaxed">{desc}</p>
  </div>
);

/* ═══════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════════════ */

const TapNWinPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { setLanguage, language } = useLanguage();
  const videoRef = useRef(null);
  const [videoPlaying, setVideoPlaying] = useState(false);

  const pageLang = ROUTES[location.pathname] || 'en';
  const t = T[pageLang];

  // Sync site language for EN/FR (not ES — site doesn't have ES mode)
  useEffect(() => {
    if (pageLang === 'en' && language !== 'en') setLanguage('en');
    if (pageLang === 'fr' && language !== 'fr') setLanguage('fr');
  }, [pageLang, language, setLanguage]);

  const handlePlayVideo = () => {
    if (videoRef.current) {
      videoRef.current.play();
      setVideoPlaying(true);
    }
  };

  const scrollToVideo = () => {
    const el = document.getElementById('tnw-video');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  return (
    <div data-testid="tapnwin-page" className="bg-[#0D0D0D] min-h-screen">
      <Helmet>
        <title>{t.seoTitle}</title>
        <meta name="description" content={t.seoDesc} />
      </Helmet>
      <FloatingNav sections={NAV_SECTIONS} lang={pageLang} />

      {/* ── 1. HERO ── */}
      <section className="relative pt-16 pb-16 lg:pt-24 lg:pb-20 overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-wine/8 rounded-full blur-[120px]" />
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge><Trophy className="w-3.5 h-3.5" strokeWidth={1.5} /> Tap'N Win</Badge>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold text-white leading-[1.1] mb-4">
            {t.heroTitle}
          </h1>
          <p className="text-lg sm:text-xl text-white/60 leading-relaxed mb-10 max-w-2xl mx-auto">
            {t.heroSub}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="btn-get-started rounded-lg px-8 py-5 text-base font-medium">
                {t.heroCta}
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
            <Button
              variant="outline"
              className="bg-white/5 text-white border-white/15 hover:bg-white/10 rounded-lg px-8 py-5 text-base font-medium"
              onClick={scrollToVideo}
            >
              <Play className="w-4 h-4 mr-2" /> {t.heroVideo}
            </Button>
          </div>
        </div>
      </section>

      {/* ── 2. WHAT IS TAP'N WIN ── */}
      <section id="tnw-what" className="py-16 lg:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge>{t.whatBadge}</Badge>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-white mb-6">{t.whatTitle}</h2>
            <div className="max-w-3xl mx-auto space-y-4 text-white/50 leading-relaxed">
              <p>{t.whatP1}</p>
              <p>{t.whatP2}</p>
              <p>{t.whatP3}</p>
            </div>
          </div>
          <CardGrid layout="2-4" testId="tnw-grid-what">
            {t.whatCards.map((c, i) => (
              <div key={i} className="bg-white/[0.03] border border-white/10 rounded-xl p-5 text-center">
                <c.icon className="w-6 h-6 text-wine mx-auto mb-3" strokeWidth={1.5} />
                <p className="text-white/70 text-sm font-medium">{c.t}</p>
              </div>
            ))}
          </CardGrid>
        </div>
      </section>

      {/* ── 3. VIDEO ── */}
      <section id="tnw-video" className="py-16 lg:py-20 bg-white/[0.02]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge>{t.videoBadge}</Badge>
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-white mb-4">{t.videoTitle}</h2>
          {t.videoNote && <p className="text-white/40 text-sm mb-6">{t.videoNote}</p>}
          <div className="mx-auto" style={{ maxWidth: 320 }}>
            <div className="relative bg-black rounded-[2rem] border-4 border-white/10 overflow-hidden shadow-2xl" style={{ aspectRatio: '9/16' }}>
              <video
                ref={videoRef}
                src={VIDEO_URL}
                className="w-full h-full object-cover"
                playsInline
                controls={videoPlaying}
                preload="metadata"
                poster=""
                onPlay={() => setVideoPlaying(true)}
              />
              {!videoPlaying && (
                <button
                  onClick={handlePlayVideo}
                  className="absolute inset-0 flex items-center justify-center bg-black/40 hover:bg-black/30 transition-colors"
                  aria-label="Play video"
                >
                  <div className="w-16 h-16 rounded-full bg-wine/90 flex items-center justify-center">
                    <Play className="w-7 h-7 text-white ml-1" fill="white" />
                  </div>
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── 4. HOW IT WORKS ── */}
      <section id="tnw-how" className="py-16 lg:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge>{t.howBadge}</Badge>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-white">{t.howTitle}</h2>
          </div>
          <CardGrid layout="1-2-4" testId="tnw-grid-how">
            {t.howSteps.map((s) => (
              <div key={s.num} className="bg-white/[0.03] border border-white/10 rounded-2xl p-6 relative">
                <span className="text-5xl font-heading font-bold text-wine/20 absolute top-4 right-5">{s.num}</span>
                <h3 className="text-white font-medium text-lg mb-3 mt-6">{s.title}</h3>
                <p className="text-white/50 text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </CardGrid>
          <CardGrid layout="phones" testId="tnw-grid-phones" className="mt-10">
            {PHONES.map((p) => (
              <figure key={p.id} data-testid={`tnw-phone-${p.id}`} className="m-0 max-w-[260px] sm:max-w-none">
                <Shot src={p.src} w={p.w} h={p.h} alt={PHONE_CAP[pageLang][p.id]} testId={`tnw-img-${p.id}`} className="mx-auto" />
                <figcaption data-testid={`tnw-caption-${p.id}`} className="text-center text-white/70 text-sm mt-3">{PHONE_CAP[pageLang][p.id]}</figcaption>
              </figure>
            ))}
          </CardGrid>
        </div>
      </section>

      {/* ── 5. BAR OR HOME ── */}
      <section id="tnw-bar-home" className="py-16 lg:py-20 bg-white/[0.02]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge>{t.barHomeBadge}</Badge>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-white">{t.barHomeTitle}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-8">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-lg bg-orange-500/15 flex items-center justify-center">
                  <Tv className="w-5 h-5 text-orange-400" strokeWidth={1.5} />
                </div>
                <h3 className="text-white font-medium text-lg">{t.barTitle}</h3>
              </div>
              <ul className="space-y-3">
                {t.barItems.map((item, i) => (
                  <li key={i} className="flex gap-2 text-white/50 text-sm leading-relaxed">
                    <ChevronRight className="w-4 h-4 mt-0.5 text-wine flex-shrink-0" /> {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-8">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-lg bg-green-500/15 flex items-center justify-center">
                  <Home className="w-5 h-5 text-green-400" strokeWidth={1.5} />
                </div>
                <h3 className="text-white font-medium text-lg">{t.homeTitle}</h3>
              </div>
              <ul className="space-y-3">
                {t.homeItems.map((item, i) => (
                  <li key={i} className="flex gap-2 text-white/50 text-sm leading-relaxed">
                    <ChevronRight className="w-4 h-4 mt-0.5 text-wine flex-shrink-0" /> {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <p className="text-center text-white/40 text-sm mt-8 max-w-2xl mx-auto">{t.barHomeNote}</p>
        </div>
      </section>

      {/* ── 6. LIVE BOARDS ── */}
      <section className="py-16 lg:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge>{t.boardsBadge}</Badge>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-white">{t.boardsTitle}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {t.boardItems.map((b, i) => (
              <div key={i} data-testid={`tnw-board-${BOARD_IMGS[i].id}`}
                className={`bg-white/[0.03] border border-white/10 rounded-2xl p-4 sm:p-5 ${i === 0 ? 'md:col-span-2' : ''}`}>
                <Shot src={BOARD_IMGS[i].src} w={BOARD_IMGS[i].w} h={BOARD_IMGS[i].h} alt={b.title} testId={`tnw-img-${BOARD_IMGS[i].id}`} />
                <h3 className="text-white font-medium text-lg mt-5 mb-2">{b.title}</h3>
                <p className="text-white/50 text-sm leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
          <p className="text-center text-white/40 text-sm mt-8">{t.boardExtra}</p>
        </div>
      </section>

      {/* ── 7. FOR BARS AND RESTAURANTS ── */}
      <section id="tnw-venues" className="py-16 lg:py-20 bg-white/[0.02]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge>{t.venBadge}</Badge>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-white">{t.venTitle}</h2>
          </div>
          <CardGrid layout="1-2-3" testId="tnw-grid-venue" className="mb-12">
            {t.venItems.map((v, i) => (
              <SectionCard key={i} icon={v.icon} title={v.title} desc={v.desc} />
            ))}
          </CardGrid>
          <div className="max-w-2xl mx-auto mb-12">
            <Shot src="tapnwin_return_gift_card.png" w={1208} h={686} alt={ALT[pageLang].gift} testId="tnw-img-return-gift-card" />
          </div>
          {/* Owner tools */}
          <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-8">
            <div className="flex items-center gap-3 mb-6">
              <BarChart3 className="w-5 h-5 text-wine" strokeWidth={1.5} />
              <h3 className="text-white font-medium text-lg">{t.venOwnerTitle}</h3>
            </div>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {t.venOwnerItems.map((item, i) => (
                <li key={i} className="flex gap-2 text-white/50 text-sm leading-relaxed">
                  <ChevronRight className="w-4 h-4 mt-0.5 text-wine flex-shrink-0" /> {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ── 8. SPONSOR OPPORTUNITY ── */}
      <section id="tnw-sponsors" className="py-16 lg:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge>{t.sponBadge}</Badge>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-white">{t.sponTitle}</h2>
          </div>
          <CardGrid layout="1-2-3" testId="tnw-grid-sponsor" className="mb-10">
            {t.sponItems.map((s, i) => (
              <SectionCard key={i} icon={s.icon} title={s.title} desc={s.desc} />
            ))}
          </CardGrid>
          <div className="text-center">
            <Link to="/contact">
              <Button className="btn-demo-cta rounded-lg px-8 py-5 text-base font-medium">
                {t.sponCta} <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ── 9. BUILT FOR REAL VENUES ── */}
      <section className="py-16 lg:py-20 bg-white/[0.02]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge>{t.builtBadge}</Badge>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-white">{t.builtTitle}</h2>
          </div>
          <CardGrid layout="1-3" testId="tnw-grid-built">
            {t.builtItems.map((b, i) => (
              <SectionCard key={i} icon={b.icon} title={b.title} desc={b.desc} />
            ))}
          </CardGrid>
        </div>
      </section>

      {/* ── 10. CLOSING CTA ── */}
      <section className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge>{t.closeBadge}</Badge>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-white mb-8">{t.closeTitle}</h2>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-6">
            <Link to="/contact">
              <Button className="btn-get-started rounded-lg px-8 py-5 text-base font-medium">
                {t.closeCta} <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
          <a href={`mailto:${t.closeEmail}`} className="text-white/40 hover:text-white/70 text-sm transition-colors">
            {t.closeEmail}
          </a>
        </div>
      </section>

      {/* ── 11. SMALL PRINT + LANG LINKS ── */}
      <section className="pb-12 lg:pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-white/25 text-xs leading-relaxed mb-4">{t.smallPrint}</p>
          <p className="text-white/30 text-xs">
            {t.langLabel}{' '}
            {t.langLinks.map((l, i) => (
              <span key={l.path}>
                {i > 0 && ' · '}
                <Link to={l.path} className="text-wine/70 hover:text-wine underline">{l.label}</Link>
              </span>
            ))}
          </p>
        </div>
      </section>
    </div>
  );
};

export default TapNWinPage;
